import { Logo, Skeleton, formatUsd } from "@unizpay/ui";
import { Lock } from "lucide-react";
import type { ReactNode } from "react";
import type { CheckoutSession } from "../types";

function TestBadge() {
  return (
    <span className="rounded-full bg-test px-2 py-0.5 text-[0.625rem] font-semibold tracking-wide text-test-fg uppercase">
      Test
    </span>
  );
}

/** Paid, as far as the payer is concerned (as on the success screen). */
const PAID: ReadonlySet<CheckoutSession["status"]> = new Set([
  "confirmed",
  "swapping",
  "settled",
  "settled_raw",
]);

/** Merchant bar and the amount block at the top of the card. */
function CardHeader({ session }: { session: CheckoutSession | undefined }) {
  if (!session) {
    return (
      <output aria-label="Loading payment" className="block px-6 pt-5 pb-6">
        <Skeleton className="h-5 w-32" />
        <Skeleton className="mx-auto mt-7 h-10 w-40" />
      </output>
    );
  }
  return (
    <div className="px-6 pt-5 pb-6">
      <div className="flex items-center gap-2.5">
        <span
          aria-hidden="true"
          className="flex size-8 shrink-0 items-center justify-center rounded-full bg-accent text-sm font-semibold text-accent-fg"
        >
          {session.merchant_name.charAt(0).toUpperCase()}
        </span>
        <span className="min-w-0 truncate text-sm font-semibold">{session.merchant_name}</span>
        {!session.livemode && <TestBadge />}
      </div>

      <div className="mt-5 text-center">
        {/* Paid short: what is left to pay leads, with the total and what arrived under it. */}
        <p className="text-xs font-medium tracking-wide text-fg-muted uppercase">
          {session.balance ? "Remaining" : PAID.has(session.status) ? "Amount paid" : "Amount due"}
        </p>
        <p className="mt-1 text-[2.5rem] leading-none font-bold tracking-tight tabular-nums">
          {formatUsd(session.balance?.remaining ?? session.amount, session.currency)}
        </p>
        {session.balance && (
          <p className="mt-2 text-sm text-fg-muted tabular-nums">
            of {formatUsd(session.balance.total, session.currency)} total ·{" "}
            {formatUsd(session.balance.paid, session.currency)} paid
          </p>
        )}
        {session.description && (
          <p className="mx-auto mt-2 max-w-xs text-sm text-fg-muted">{session.description}</p>
        )}
      </div>
    </div>
  );
}

/**
 * The hosted payment page: one floating card centered on a soft gradient, on every screen
 * size. Merchant and amount on top, then the current step.
 */
export function CheckoutShell({
  session,
  backUrl = null,
  children,
}: {
  session: CheckoutSession | undefined;
  /** Where "cancel" leaves to (the merchant's cancel URL), while the payment is open. */
  backUrl?: string | null;
  children: ReactNode;
}) {
  return (
    <div className="relative min-h-dvh overflow-hidden bg-bg">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 top-0 h-[28rem] bg-[radial-gradient(60%_100%_at_50%_0%,color-mix(in_oklab,var(--brand)_10%,transparent),transparent)]"
      />
      <main className="relative mx-auto flex min-h-dvh w-full max-w-[28rem] flex-col px-4 py-8 sm:py-14">
        <div className="overflow-hidden rounded-2xl border border-line bg-surface shadow-[0_24px_60px_-20px_rgb(0_0_0/0.25)]">
          <CardHeader session={session} />
          <div className="border-t border-dashed border-line-strong px-6 pt-6 pb-7">
            {children}
            {backUrl && session && (
              <p className="mt-6 text-center">
                <a
                  href={backUrl}
                  className="rounded text-[0.8125rem] text-fg-muted underline-offset-2 hover:text-fg hover:underline"
                >
                  Cancel and return to {session.merchant_name}
                </a>
              </p>
            )}
          </div>
        </div>

        <footer className="mt-6 flex justify-center text-xs text-fg-muted">
          <p className="flex items-center gap-1.5">
            <Lock aria-hidden="true" className="size-3" />
            Secured by <Logo className="h-4" />
          </p>
        </footer>
      </main>
    </div>
  );
}
